import React from "react";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useMediaQuery } from "@mui/material";
import {
    faInbox,
    faChartLine,
    faUser,
    faCreditCard,
    faQuestionCircle,
    faFileAlt,
    faMoon,
    faSun,
    faTimes,
    faBriefcase,
    faArchive,
    faChevronLeft,
    faChevronRight,
    faPalette,
} from "@fortawesome/free-solid-svg-icons";
import Logo from "./Logo";
import { useAppBootstrap } from "../context/AppBootstrapContext";
import { maybeRenderOperatorClassificationAuditNav } from "./operatorClassificationAuditNavEntry";
import { maybeRenderOperatorMailboxSyncDiagnosticsNav } from "./operatorMailboxSyncDiagnosticsNavEntry";
import { maybeRenderOperatorRemediationPreviewNav } from "./operatorRemediationPreviewNavEntry";

interface SidebarProps {
    isDarkMode: boolean;
    toggleTheme: () => void;
    isOpen: boolean;
    onClose: () => void;
    isCollapsed?: boolean;
    toggleCollapse?: () => void;
    /** Below 1024 px the menu carries the Color legend (the Today header keeps it at 1024 px and up). */
    onOpenColorLegend?: () => void;
}

type NavItemDef = {
    name: string;
    icon: any;
    path: string;
    testId: string;
};

function cx(...classes: Array<string | false | null | undefined>) {
    return classes.filter(Boolean).join(" ");
}

/**
 * Sidebar.tsx (drop-in replacement)
 * Goals:
 * - Collapsed sidebar becomes a clean icon rail (no style breakage).
 * - Uses your theme tokens (bg-theme-secondary, border-theme, text-theme, bg-accent-muted).
 * - Keeps mobile overlay + desktop fixed behavior.
 * - Removes MUI Tooltip (keeps only useMediaQuery) to avoid mixed styling inconsistencies.
 * - Better focus/hover states + accessibility (aria + keyboard friendly).
 */
const Sidebar: React.FC<SidebarProps> = ({
    isDarkMode,
    toggleTheme,
    isOpen,
    onClose,
    isCollapsed = false,
    toggleCollapse,
    onOpenColorLegend,
}) => {
    const isDesktop = useMediaQuery("(min-width: 1024px)", { noSsr: true });
    const isCollapsedDesktop = Boolean(isCollapsed && isDesktop);
    const { bootstrap } = useAppBootstrap();

    const mainNavItems: NavItemDef[] = [
        { name: "Today", icon: faInbox, path: "/app/today", testId: "NAV_TODAY" },
        { name: "Workbench", icon: faBriefcase, path: "/app/workbench", testId: "NAV_WORKBENCH" },
        { name: "Archive", icon: faArchive, path: "/app/archive", testId: "NAV_ARCHIVE" },
        { name: "Intake", icon: faFileAlt, path: "/app/documents", testId: "NAV_DOCUMENTS" },
        { name: "Insights", icon: faChartLine, path: "/app/statistics", testId: "NAV_INSIGHTS" },
        { name: "Billing", icon: faCreditCard, path: "/app/billing", testId: "NAV_BILLING" },
    ];

    const secondaryNavItems: NavItemDef[] = [
        { name: "Profile", icon: faUser, path: "/app/profile", testId: "NAV_PROFILE" },
        { name: "Help", icon: faQuestionCircle, path: "/app/help", testId: "NAV_HELP" },
    ];

    const closeOnMobileNav = () => {
        // Keep sidebar open on desktop; close only on mobile/tablet.
        if (!isDesktop) onClose();
    };

    const TooltipWrap = ({
        label,
        children,
    }: {
        label: string;
        children: React.ReactNode;
    }) => {
        // Only show custom tooltip when it's a collapsed desktop rail.
        if (!isCollapsedDesktop) return <>{children}</>;

        return (
            <div className="relative group flex w-full justify-center">
                {children}
                <div
                    className={cx(
                        "pointer-events-none absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2",
                        "opacity-0 translate-x-[-4px] group-hover:opacity-100 group-hover:translate-x-0",
                        "transition-all duration-150"
                    )}
                    role="tooltip"
                >
                    <div className="whitespace-nowrap rounded-lg border border-theme bg-theme-secondary px-2.5 py-1.5 text-xs text-theme shadow-lg">
                        {label}
                    </div>
                </div>
            </div>
        );
    };

    const NavItem = ({ item }: { item: NavItemDef }) => {
        return (
            <TooltipWrap label={item.name}>
                <NavLink
                    to={item.path}
                    aria-label={item.name}
                    title={isCollapsedDesktop ? item.name : undefined} // native tooltip fallback
                    onClick={closeOnMobileNav}
                    data-testid={item.testId}
                    className={({ isActive }) =>
                        cx(
                            "relative w-full select-none",
                            "flex items-center rounded-xl",
                            isCollapsedDesktop ? "justify-center px-0" : "justify-start gap-3 px-3",
                            "py-2.5",
                            "transition-colors duration-150",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]",
                            isActive
                                ? "bg-accent-muted text-theme"
                                : "text-theme-secondary hover:text-theme hover:bg-theme-card-hover"
                        )
                    }
                >
                    {({ isActive }) => (
                        <>
                            {/* Active indicator */}
                            <span
                                className={cx(
                                    "absolute left-0 top-1/2 -translate-y-1/2",
                                    "w-[3px] h-4 rounded-r-full",
                                    "transition-all duration-150",
                                    isActive ? "bg-accent opacity-100" : "opacity-0"
                                )}
                            />

                            <FontAwesomeIcon
                                icon={item.icon}
                                className={cx(
                                    "w-[18px] h-[18px] shrink-0",
                                    "transition-colors duration-150",
                                    isActive ? "text-accent" : ""
                                )}
                            />

                            {!isCollapsedDesktop && (
                                <span className="text-[13px] font-medium leading-none">{item.name}</span>
                            )}
                        </>
                    )}
                </NavLink>
            </TooltipWrap>
        );
    };

    return (
        <>
            {/* Mobile Backdrop */}
            {isOpen && (
                <div
                    data-testid="NAV_BACKDROP"
                    className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden"
                    onClick={onClose}
                />
            )}

            {/* Sidebar */}
            <aside
                id="sidebar-nav"
                className={cx(
                    "fixed left-0 top-0 z-50 h-screen",
                    "flex flex-col overflow-hidden",
                    "transition-[width,transform] duration-200 ease-out",
                    isOpen ? "translate-x-0" : "-translate-x-full",
                    "lg:translate-x-0",
                    isCollapsedDesktop ? "w-16" : "w-[220px]",
                    "bg-theme-secondary border-r border-theme"
                )}
            >
                {/* Header */}
                <div
                    className={cx(
                        "h-14 shrink-0",
                        "flex items-center",
                        isCollapsedDesktop ? "justify-center px-2" : "justify-between px-4",
                        "border-b border-theme"
                    )}
                >
                    <div className="flex items-center">
                        <Logo variant={isCollapsedDesktop ? 'symbol' : 'wordmark'} size={24} />
                    </div>

                    {!isCollapsedDesktop && (
                        <button
                            onClick={onClose}
                            className="lg:hidden p-2 text-theme-muted hover:text-theme rounded-lg hover:bg-theme-card-hover transition-colors"
                            data-testid="NAV_MOBILE_CLOSE"
                            aria-label="Close menu"
                            type="button"
                        >
                            <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                        </button>
                    )}
                </div>

                {/* Navigation */}
                <div className={cx("flex-1 overflow-y-auto overflow-x-hidden", isCollapsedDesktop ? "px-2" : "px-3")}>
                    {/* Main Nav */}
                    <nav className="flex flex-col gap-1 py-3" aria-label="Main navigation">
                        {mainNavItems.map((item) => (
                            <NavItem key={item.path} item={item} />
                        ))}
                    </nav>

                    {/* Divider */}
                    <div className="h-px bg-[var(--color-border)]/70 my-2" />

                    {/* Secondary Nav */}
                    <nav className="flex flex-col gap-1 py-2" aria-label="Secondary navigation">
                        {maybeRenderOperatorClassificationAuditNav({
                            bootstrap,
                            isCollapsedDesktop,
                            onNavigate: closeOnMobileNav,
                        })}
                        {maybeRenderOperatorMailboxSyncDiagnosticsNav({
                            bootstrap,
                            isCollapsedDesktop,
                            onNavigate: closeOnMobileNav,
                        })}
                        {maybeRenderOperatorRemediationPreviewNav({
                            bootstrap,
                            isCollapsedDesktop,
                            onNavigate: closeOnMobileNav,
                        })}
                        {secondaryNavItems.map((item) => (
                            <NavItem key={item.path} item={item} />
                        ))}
                    </nav>
                </div>

                {/* Footer */}
                <div className={cx("shrink-0 pb-3", isCollapsedDesktop ? "px-2" : "px-3")}>
                    {/* Collapse Toggle (desktop only) */}
                    {toggleCollapse && isDesktop && (
                        <TooltipWrap label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}>
                            <button
                                onClick={toggleCollapse}
                                aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                                aria-expanded={!isCollapsed}
                                aria-controls="sidebar-nav"
                                className={cx(
                                    "w-full rounded-xl",
                                    "flex items-center",
                                    isCollapsedDesktop ? "justify-center px-0" : "justify-start gap-3 px-3",
                                    "py-2.5",
                                    "text-theme-muted hover:text-theme",
                                    "hover:bg-theme-card-hover transition-colors duration-150",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
                                )}
                                type="button"
                                title={isCollapsedDesktop ? (isCollapsed ? "Expand" : "Collapse") : undefined}
                            >
                                <FontAwesomeIcon
                                    icon={isCollapsed ? faChevronRight : faChevronLeft}
                                    className="w-3.5 h-3.5"
                                />
                                {!isCollapsedDesktop && <span className="text-xs font-medium">Collapse</span>}
                            </button>
                        </TooltipWrap>
                    )}

                    {/* Theme Toggle: at 1024 px and up; below it the top bar has the theme button */}
                    {isDesktop && (
                        <TooltipWrap label={isDarkMode ? "Dark mode" : "Light mode"}>
                            <button
                                data-testid="NAV_THEME_TOGGLE"
                                onClick={toggleTheme}
                                aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                                className={cx(
                                    "mt-2 w-full rounded-xl",
                                    "flex items-center",
                                    isCollapsedDesktop ? "justify-center px-0" : "justify-between px-3",
                                    "py-2.5",
                                    "border border-theme bg-theme-card hover:bg-theme-card-hover",
                                    "text-theme-secondary hover:text-theme transition-colors duration-150",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
                                )}
                                type="button"
                            >
                                <div className={cx("flex items-center", isCollapsedDesktop ? "" : "gap-2.5")}>
                                    <FontAwesomeIcon
                                        icon={isDarkMode ? faMoon : faSun}
                                        className={cx(
                                            "w-4 h-4",
                                            isDarkMode ? "text-[var(--color-accent-indigo)]" : "text-amber-400"
                                        )}
                                    />
                                    {!isCollapsedDesktop && (
                                        <span className="text-xs font-medium">{isDarkMode ? "Dark" : "Light"}</span>
                                    )}
                                </div>

                                {!isCollapsedDesktop && (
                                    <div
                                        className={cx(
                                            "w-9 h-5 rounded-full p-0.5 transition-colors",
                                            isDarkMode ? "bg-[var(--color-accent-muted)]" : "bg-theme-tertiary"
                                        )}
                                    >
                                        <div
                                            className={cx(
                                                "w-4 h-4 rounded-full bg-white transition-transform",
                                                isDarkMode ? "translate-x-4" : "translate-x-0"
                                            )}
                                        />
                                    </div>
                                )}
                            </button>
                        </TooltipWrap>
                    )}

                    {/* Color legend: phones only; at 1024 px and up it stays in the Today header */}
                    {!isDesktop && onOpenColorLegend && (
                        <button
                            data-testid="NAV_COLOR_LEGEND"
                            onClick={() => {
                                onClose();
                                onOpenColorLegend();
                            }}
                            className={cx(
                                "mt-2 w-full rounded-xl",
                                "flex items-center gap-2.5 px-3",
                                "py-2.5",
                                "border border-theme bg-theme-card hover:bg-theme-card-hover",
                                "text-theme-secondary hover:text-theme transition-colors duration-150",
                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
                            )}
                            type="button"
                        >
                            <FontAwesomeIcon icon={faPalette} className="w-4 h-4" aria-hidden />
                            <span className="text-xs font-medium">Color legend</span>
                        </button>
                    )}
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
